import ABTestEnabled from '@components/ABTestEnabled/ABTestEnabled';
import AddressForm from '@components/AddressForm';
import {
  ABTEST_CHECKOUT_INPUTS_ORDERING,
  COUNTRY_BY_CODE,
} from '@monofront/business/constants';
import {
  DeliveryBarMachineContext,
  useCheckoutCartState,
} from '@monofront/business/contexts';
import { CHECKOUT_TRANSITIONS } from '@monofront/business/stateMachines/checkout';
import {
  useCheckoutCartMachine,
  useCheckoutSelectAddressMachine,
} from '@context/checkoutCartMachineContext';
import {
  IShippingAddressSelectedEvent,
  SELECT_ADDRESS_TRANSITIONS,
  SELECT_ADDRESS_STATES,
} from '@monofront/business/stateMachines';
import {
  ADDRESS_MODAL_STATES,
  ADDRESS_MODAL_TRANSITIONS,
} from '@monofront/business/stateMachines/checkout/selectAddress/addressModal';
import HeartLoader from '@monofront/design-system/components/HeartLoader/HeartLoader';
import { CHECKOUT_TAGS_ADDRESS_FORM } from '@monofront/business/stateMachines/checkout/tags';
import AddButtonWithLabel from '@monofront/design-system/components/AddButtonWithLabel';
import RadioLine from '@monofront/design-system/components/Form/RadioLine';
import Modal from '@monofront/design-system/organisms/Modal';
import Edit from '@monofront/design-system/visuals/Icons/Old/Edit';
import { IAddressV2 } from '@monofront/types';
import useI18n from '@hooks/i18n/useI18n';
import { useTranslations } from 'next-intl';
import { shake } from 'radash';
import { useEffect } from 'react';
import { useEligibility } from '@monofront/business/contexts';
import AddressFormABTest from '@components/AddressForm/AddressFormABTest';
import { EVENTS } from '@monofront/business/stateMachines/deliveryBar';
import { omit } from '@monofront/business/utils';

const getSelectActionName = (isForBillingAddress) =>
  isForBillingAddress
    ? SELECT_ADDRESS_TRANSITIONS.SELECT_BILLING_ADDRESS
    : SELECT_ADDRESS_TRANSITIONS.SELECT_SHIPPING_ADDRESS;

interface IAddressesListAndFormProps {
  selectIdPrefix?: string;
  isForBillingAddress?: boolean;
}

const AddressesListAndForm: React.FC<IAddressesListAndFormProps> = ({
  selectIdPrefix = '',
  isForBillingAddress = false,
}) => {
  const { state } = useCheckoutCartMachine();
  const {
    selectAddressState,
    sendToSelectAddress,
    addressModalState,
    sendToAddressModal,
  } = useCheckoutSelectAddressMachine();
  const { send: sendToDeliveryBarMachine } =
    DeliveryBarMachineContext.useActorRef();
  const { setIsScrollAvailable } = useCheckoutCartState();
  const { address } = useEligibility();
  const { addresses } = selectAddressState.context;
  const { countryCode } = useI18n();

  const filteredDefaultValues = shake({
    countryCode,
    street: address?.street,
    postcode: address?.postalCode,
    city: address?.city,
  });

  const selectedHash = isForBillingAddress
    ? selectAddressState.context.billingAddressSelected?.hash
    : selectAddressState.context.shippingAddressSelected?.hash;

  const tShipping = useTranslations('Shipping');

  useEffect(() => {
    return () => {
      setIsScrollAvailable(false);
    };
  }, []);

  useEffect(() => {
    if (
      !isForBillingAddress &&
      selectAddressState.context.shippingAddressSelected?.hash
    ) {
      const address = selectAddressState.context.shippingAddressSelected;

      sendToDeliveryBarMachine({
        type: EVENTS.OVERWRITE_ADDRESS,
        address: {
          street: address.street,
          city: address.city,
          postalCode: address.postcode,
          country: address.countryCode,
        },
      });
    }
  }, [selectAddressState.context.shippingAddressSelected?.hash]);

  if (
    selectAddressState.matches({
      [SELECT_ADDRESS_STATES.GET]: SELECT_ADDRESS_STATES.ADDRESSES_FETCHING,
    }) ||
    selectAddressState.matches({
      [SELECT_ADDRESS_STATES.GET_WITH_FORM]:
        SELECT_ADDRESS_STATES.ADDRESSES_FETCHING,
    })
  ) {
    return <HeartLoader />;
  }

  return (
    <>
      <Modal
        isOpen={
          addressModalState?.matches?.(ADDRESS_MODAL_STATES.OPEN) &&
          addressModalState.context.isForBillingAddress === isForBillingAddress
        }
        UNSAFE_className="modal"
        externalToggle={() =>
          sendToAddressModal({
            type: ADDRESS_MODAL_TRANSITIONS.CLOSE,
          })
        }
        hasPadding
        title={
          addressModalState?.context?.update
            ? tShipping('updateAddress')
            : tShipping('addMyAddress')
        }
      >
        <ABTestEnabled.Provider feature={ABTEST_CHECKOUT_INPUTS_ORDERING}>
          <ABTestEnabled.A>
            <AddressForm
              isLoading={selectAddressState?.matches?.(
                SELECT_ADDRESS_STATES.ADDRESS_CREATING,
              )}
              onSubmitAddress={(address: IAddressV2) => {
                sendToAddressModal({
                  type: ADDRESS_MODAL_TRANSITIONS.SUBMIT,
                  address,
                  creatingForBillingOnly: isForBillingAddress,
                });
              }}
              onCancel={() =>
                sendToAddressModal({
                  type: ADDRESS_MODAL_TRANSITIONS.CLOSE,
                })
              }
              defaultValues={addressModalState?.context?.defaultAddress}
              hasCancelButton
            />
          </ABTestEnabled.A>
          <ABTestEnabled.B>
            <AddressFormABTest
              isLoading={selectAddressState?.matches?.(
                SELECT_ADDRESS_STATES.ADDRESS_CREATING,
              )}
              onSubmitAddress={(address: IAddressV2) => {
                sendToAddressModal({
                  type: ADDRESS_MODAL_TRANSITIONS.SUBMIT,
                  address,
                  creatingForBillingOnly: isForBillingAddress,
                });
              }}
              onCancel={() =>
                sendToAddressModal({
                  type: ADDRESS_MODAL_TRANSITIONS.CLOSE,
                })
              }
              defaultValues={addressModalState?.context?.defaultAddress}
              hasCancelButton
            />
          </ABTestEnabled.B>
        </ABTestEnabled.Provider>
      </Modal>

      <ul className="address-list">
        {addresses.map((address) => (
          <li key={address.id}>
            <RadioLine
              disabled={
                !selectAddressState.can({
                  type: getSelectActionName(isForBillingAddress),
                  addressSelected: address,
                } as IShippingAddressSelectedEvent) ||
                !state.can({
                  type: CHECKOUT_TRANSITIONS.SELECT_SHIPPING_ADDRESS,
                  shippingAddress: {} as IAddressV2,
                  billingAddress: {} as IAddressV2,
                })
              }
              id={`${selectIdPrefix}-${address.id}`}
              name="address"
              required
              value={address.id!}
              checked={address.hash === selectedHash}
              handleChecked={(e) => {
                e?.stopPropagation();
                setIsScrollAvailable(true);
                sendToSelectAddress({
                  type: getSelectActionName(isForBillingAddress),
                  addressSelected: address,
                });
              }}
              more={
                <button
                  className="pointer update-address-button"
                  onClick={(e) => {
                    e.stopPropagation();
                    sendToAddressModal({
                      type: ADDRESS_MODAL_TRANSITIONS.OPEN,
                      defaultAddress: omit(address, 'hash'),
                      isForBillingAddress,
                    });
                  }}
                  data-testid={`AddressesListAndForm-Button-edit-address-${selectIdPrefix}-${address.id}`}
                >
                  <Edit />
                </button>
              }
              dataTestId={`AddressesListAndForm-Button-select-address-${selectIdPrefix}-${address.id}`}
            >
              <span className="address-details">
                <span>
                  {address.firstName} {address.lastName}
                </span>
                {address?.company && <span>{address.company}</span>}
                <span>{address.street}</span>
                <span>
                  {address.postcode} {address.city}{' '}
                  {COUNTRY_BY_CODE[address.countryCode] || ''}
                </span>
                <span>{address.phoneNumber}</span>
              </span>
            </RadioLine>
          </li>
        ))}
        {selectAddressState.tags.has(CHECKOUT_TAGS_ADDRESS_FORM) ? (
          <li className="address-row new-address-block">
            {addresses.length > 0 && (
              <RadioLine
                id="new-address"
                name="new-address"
                required
                value="new-address"
                checked={true}
              >
                <span className="bold">{tShipping('addANewAddress')}</span>
              </RadioLine>
            )}
            <ABTestEnabled.Provider feature={ABTEST_CHECKOUT_INPUTS_ORDERING}>
              <ABTestEnabled.A>
                <AddressForm
                  isLoading={selectAddressState?.matches?.(
                    SELECT_ADDRESS_STATES.ADDRESS_CREATING,
                  )}
                  defaultValues={filteredDefaultValues}
                  onSubmitAddress={(address: IAddressV2) => {
                    sendToSelectAddress({
                      type: SELECT_ADDRESS_TRANSITIONS.CREATE_ADDRESS,
                      address,
                      creatingForBillingOnly: isForBillingAddress,
                    });
                  }}
                />
              </ABTestEnabled.A>
              <ABTestEnabled.B>
                <AddressFormABTest
                  isLoading={selectAddressState?.matches?.(
                    SELECT_ADDRESS_STATES.ADDRESS_CREATING,
                  )}
                  defaultValues={filteredDefaultValues}
                  onSubmitAddress={(address: IAddressV2) => {
                    sendToSelectAddress({
                      type: SELECT_ADDRESS_TRANSITIONS.CREATE_ADDRESS,
                      address,
                      creatingForBillingOnly: isForBillingAddress,
                    });
                  }}
                />
              </ABTestEnabled.B>
            </ABTestEnabled.Provider>
          </li>
        ) : (
          <li
            className={`pointer address-row-add ${
              addressModalState.matches(ADDRESS_MODAL_STATES.OPEN)
                ? 'colored-bg'
                : ''
            }`}
          >
            <div className="add-address-button">
              <AddButtonWithLabel
                handleClick={() => {
                  sendToAddressModal({
                    type: ADDRESS_MODAL_TRANSITIONS.OPEN,
                    isForBillingAddress,
                  });
                }}
                dataTestId={`AddressesListAndForm-AddButtonWithLabel-Button-add-address`}
              >
                {tShipping('addANewAddress')}
              </AddButtonWithLabel>
            </div>
          </li>
        )}
      </ul>

      <style jsx>{`
        li {
          list-style: none;
        }

        :global(.modal) {
          top: 10%;
        }

        .title {
          margin: var(--space-5) 0;
        }

        .address-list {
          margin: var(--space-3) 0;
        }

        .address-row-add {
          margin: var(--space-3);
        }

        .address-row {
          display: grid;
          padding: var(--space-3);
          grid-template-areas: 'address-radio-button address-details update-address-button';
          grid-column-gap: var(--space-4);
          grid-template-columns: 25px 1fr 32px;
        }

        .address-row :global(input + div) {
          width: 24px;
          height: 24px;
        }

        .colored-bg {
          background-color: var(--clr-primary-5);
          border-radius: var(--rd-2);
        }

        .address-details-and-update-pen {
          display: flex;
          flex-direction: row;
          justify-content: space-between;
          width: 100%;
        }

        .update-address-button {
          height: fit-content;
          grid-area: update-address-button;
          background: none;
          border: none;
        }

        .address-details {
          display: flex;
          flex-direction: column;
          grid-area: address-details;
        }

        .address-radio-button {
          grid-area: address-radio-button;
        }

        .address-details h2 {
          margin-bottom: var(--space-2);
        }

        .add-address-button {
          display: flex;
          align-items: center;
        }

        .add-address-button > span {
          margin-left: var(--space-4);
        }

        .add-address-button :global(.btn-plus) {
          width: 24px;
          height: 24px;
          line-height: 24px;
          font-size: var(--font-size-4);
        }

        .new-address-block {
          display: flex;
          flex-direction: column;
          margin-bottom: var(--space-5);
          padding: 0;
        }

        .new-address-block :global(label) {
          margin-bottom: var(--space-2);
        }
      `}</style>
    </>
  );
};

export default AddressesListAndForm;
